import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { TabPanel, TabView } from "primereact/tabview";
import { useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import BaseRateFilesTable from "./components/BaseRateFilesTable";
import ConfigsTable from "./components/ConfigsTable";
import ContractsTable from "./components/ContractsTable";
import CustomCptVolumeTable from "./components/CustomCptVolumeTable";
import EdiConversionFilesTable from "./components/EdiConversionFilesTable";
import MarketPricesTable from "./components/MarketPricesTable";
import PayerRulesTable from "./components/PayerRulesTable";
import PayersTable from "./components/PayersTable";
import GrouperRateFile from "./tools/GrouperRateFile";
import RateExpander from "./tools/RateExpander";
import RateFileSplitter from "./tools/RateFileSplitter";
import ServiceLineLibraries from "./tools/ServiceLineLibraries";
import ServiceLineSynonyms from "./tools/ServiceLineSynonyms";
import {
  BASE_RATE_SUBTAB_CONFIGS,
  BASE_RATE_SUBTAB_CUSTOM_CPT_VOLUME,
  BASE_RATE_SUBTAB_FILES,
  BASE_RATE_TAB_INDEX,
  TOOLS_SUBTAB_EXPANDER,
  TOOLS_SUBTAB_LIBRARIES,
  TOOLS_SUBTAB_SPLITTER,
  TOOLS_SUBTAB_SYNONYMS,
  TOOLS_SUBTABS,
  TOOLS_TAB_INDEX,
  ToolsSubtabValue,
} from "./utils/constants";

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

const StyledMenu = styled(Menu)`
  /* CLE-2595 — the group headers carry the capability matrix ("what can this rule type
     be?"), so they have to be legible at a glance: a tinted band with dark text, not faint
     grey lettering that reads as decoration. Width fits the longest header on one line and
     no more, so the popup doesn't cover the table behind it. */
  width: auto !important;
  min-width: 17rem !important;
  padding: 0 !important;
  overflow: hidden;

  .p-submenu-header {
    padding: 0.5rem 1rem !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--gray-900) !important;
    background: var(--gray-100) !important;
    border-bottom: 1px solid var(--gray-200);
    /* Each group gets its own accent stripe (set per group below) so the four blocks read
       as four different things, not one striped list. */
    border-left: 4px solid var(--gray-400);
    white-space: nowrap;
  }
  /* A thick gutter is what actually separates the blocks — a hairline reads as a row
     divider, which is exactly how the earlier version failed. Not on the first group. */
  .p-menu-list > .p-submenu-header:not(:first-child) {
    border-top: 8px solid var(--gray-200);
  }

  /* Items sit inside their group: indented, and carrying the group's stripe in a pale
     tint so the block stays visually bounded down to its last row. */
  .p-menuitem-link {
    padding: 0.45rem 1rem 0.45rem 1.35rem !important;
  }
  .p-menuitem-text {
    font-size: 14px !important;
    color: var(--gray-800) !important;
  }
  .p-menuitem-link:hover {
    background: var(--primary-50) !important;
  }
  .p-menuitem-link:hover .p-menuitem-text {
    color: var(--primary-700) !important;
  }

  /* Per-group accents. Class names come from the menu model's className property, which
     PrimeReact puts on the header list item. */
  .rule-group--contract {
    border-left-color: var(--primary-500) !important;
  }
  .rule-group--fallback {
    border-left-color: var(--teal-500) !important;
    background: var(--teal-50) !important;
  }
  .rule-group--scoped-only {
    border-left-color: var(--blue-400) !important;
  }
  .rule-group--inpatient {
    border-left-color: var(--gray-500) !important;
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: ${sizer(3)};
`;

const BASE_RATE_SUBTABS = [
  BASE_RATE_SUBTAB_FILES,
  BASE_RATE_SUBTAB_CONFIGS,
  BASE_RATE_SUBTAB_CUSTOM_CPT_VOLUME,
] as const;

export function PayerRules() {
  const navigate = useNavigate();
  const newRuleMenuRef = useRef<Menu>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabIndex = Number(searchParams.get("tab"));
  const baseSubtab = searchParams.get("baseSubtab") ?? BASE_RATE_SUBTAB_FILES;
  const baseRateSubtabIndex = Math.max(
    0,
    BASE_RATE_SUBTABS.indexOf(baseSubtab as (typeof BASE_RATE_SUBTABS)[number])
  );

  const toolsSubtab =
    (searchParams.get("toolsSubtab") as ToolsSubtabValue | null) ??
    TOOLS_SUBTAB_SPLITTER;
  const toolsSubtabIndex = Math.max(
    0,
    TOOLS_SUBTABS.indexOf(toolsSubtab as (typeof TOOLS_SUBTABS)[number])
  );

  const handleBaseRateSubtabChange = (index: number) => {
    const nextSubtab = BASE_RATE_SUBTABS[index] ?? BASE_RATE_SUBTAB_FILES;
    setSearchParams({
      tab: String(BASE_RATE_TAB_INDEX),
      baseSubtab: nextSubtab,
    });
  };

  const handleToolsSubtabChange = (index: number) => {
    const nextSubtab = TOOLS_SUBTABS[index] ?? TOOLS_SUBTAB_SPLITTER;
    setSearchParams({
      tab: String(TOOLS_TAB_INDEX),
      toolsSubtab: nextSubtab,
    });
  };

  const goToSynonymsSubtab = (_payerId?: number) => {
    // Deep-link to the Synonyms sub-tab. (Payer-id pre-filter is a follow-up; the
    // synonyms sub-tab already has a payer-filter dropdown, so the analyst can pick
    // it in two clicks.)
    setSearchParams({
      tab: String(TOOLS_TAB_INDEX),
      toolsSubtab: TOOLS_SUBTAB_SYNONYMS,
    });
  };

  // CLE-2595 — grouped by what each rule type CAN BE, using the same words the create
  // form then shows (Payer-Provider / Typical / Fallback). The capability matrix is the
  // thing nobody remembers: only three types have a fallback resolver in the pricing
  // engine, and three others can't be templates at all. Encoding it here means the
  // question is answered before you pick, not discovered on the next screen.
  // Order is by how often the group is used, not by capability — the everyday contract
  // rules stay on top where they have always been; fallback-capable types follow.
  // Source of truth: APPLICABILITY_CAPABILITIES in _shared/defaultRules.ts.
  const newRuleMenuItems = useMemo<MenuItem[]>(
    () => [
      {
        label: "Payer-Provider · Typical",
        className: "rule-group--contract",
        items: [
          { label: "MPPR", command: () => navigate("/payer-rules/mppr/new") },
          {
            label: "Case Rate",
            command: () => navigate("/payer-rules/case-rate/new"),
          },
          {
            label: "Package",
            command: () => navigate("/payer-rules/packages/new"),
          },
          {
            label: "Exploding Fee Schedule",
            command: () => navigate("/payer-rules/exploding-fee-schedules/new"),
          },
          {
            label: "Unit Cap (Once Per Service)",
            command: () => navigate("/payer-rules/unit-cap/new"),
          },
        ],
      },
      {
        label: "Payer-Provider · Typical · Fallback",
        className: "rule-group--fallback",
        items: [
          {
            label: "Professional Modifiers",
            command: () => navigate("/payer-rules/professional-modifiers/new"),
          },
          {
            label: "Lesser-Of Charges",
            command: () => navigate("/payer-rules/lesser-of-charges/new"),
          },
          {
            label: "Charge-Based Pricing",
            command: () => navigate("/payer-rules/charge-based-pricing/new"),
          },
        ],
      },
      {
        label: "Payer-Provider only",
        className: "rule-group--scoped-only",
        items: [
          {
            label: "Hierarchy",
            command: () => navigate("/payer-rules/hierarchy-rules/new"),
          },
          {
            label: "Professional",
            command: () => navigate("/payer-rules/professional-rulesets/new"),
          },
          {
            label: "Institutional",
            command: () => navigate("/payer-rules/institutional-rulesets/new"),
          },
        ],
      },
      {
        // Inpatient rulesets are Payer-Provider · Typical like the group above; they stay
        // separate because the three entries are types of ONE ruleset, not three rule
        // types. The header stays short so it doesn't force the popup wider.
        label: "Inpatient Rules",
        className: "rule-group--inpatient",
        items: [
          {
            label: "Pass Through",
            command: () =>
              navigate("/payer-rules/inpatient-rulesets/new?type=PASS_THROUGH"),
          },
          {
            label: "Transfer",
            command: () =>
              navigate("/payer-rules/inpatient-rulesets/new?type=TRANSFER"),
          },
          {
            label: "Inpatient Outlier Rules",
            command: () =>
              navigate(
                "/payer-rules/inpatient-rulesets/new?type=INPATIENT_OUTLIER"
              ),
          },
        ],
      },
    ],
    [navigate]
  );

  return (
    <TabView
      className="w-full"
      activeIndex={activeTabIndex}
      onTabChange={(e) => {
        if (e.index === BASE_RATE_TAB_INDEX) {
          setSearchParams({
            tab: e.index.toString(),
            baseSubtab: BASE_RATE_SUBTABS[baseRateSubtabIndex],
          });
        } else if (e.index === TOOLS_TAB_INDEX) {
          setSearchParams({
            tab: e.index.toString(),
            toolsSubtab: TOOLS_SUBTABS[toolsSubtabIndex],
          });
        } else {
          setSearchParams({ tab: e.index.toString() });
        }
      }}
    >
      <TabPanel header="Adjustment Rules">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            aria-controls="popup_menu_right"
            aria-haspopup
            onClick={(event) => newRuleMenuRef.current?.toggle(event)}
          >
            New Payer Rule
          </StyledButton>
          <StyledMenu
            model={newRuleMenuItems}
            popup
            ref={newRuleMenuRef}
            id="popup_menu_right"
            appendTo="self"
          />
        </FlexWrapper>
        {/* CLE-2524 — Live Rules is everything the pricing engine applies
            (payer-provider + fallback); Typical Rules are inert templates. */}
        <TabView>
          <TabPanel header="Live Rules">
            <PayerRulesTable mode="live" />
          </TabPanel>
          <TabPanel header="Typical Rules">
            <PayerRulesTable mode="typical" />
          </TabPanel>
        </TabView>
      </TabPanel>
      <TabPanel header="Payers">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/payer/new")}
          >
            New Payer
          </StyledButton>
        </FlexWrapper>
        <PayersTable />
      </TabPanel>
      <TabPanel header="Source Fee Schedules">
        <TabView
          activeIndex={baseRateSubtabIndex}
          onTabChange={(e) => handleBaseRateSubtabChange(e.index)}
        >
          <TabPanel header="Base Rate Files">
            <BaseRateFilesTable />
          </TabPanel>
          <TabPanel header="Base Rate Configs">
            <ConfigsTable />
          </TabPanel>
          <TabPanel header="Custom CPT Volume">
            <CustomCptVolumeTable />
          </TabPanel>
        </TabView>
      </TabPanel>
      <TabPanel header="Contracts">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/contracts/new")}
          >
            New Contract
          </StyledButton>
        </FlexWrapper>
        <ContractsTable />
      </TabPanel>
      <TabPanel header="Market Price Datasets">
        <MarketPricesTable />
      </TabPanel>
      <TabPanel header="EDI Files">
        <EdiConversionFilesTable />
      </TabPanel>
      <TabPanel header="Rate File Tools">
        <TabView
          activeIndex={toolsSubtabIndex}
          onTabChange={(e) => handleToolsSubtabChange(e.index)}
        >
          <TabPanel header="Splitter">
            <RateFileSplitter />
          </TabPanel>
          <TabPanel header="Expander">
            <RateExpander onNavigateToSynonyms={goToSynonymsSubtab} />
          </TabPanel>
          <TabPanel header="Groupers">
            <GrouperRateFile />
          </TabPanel>
          <TabPanel header="Service Line Synonyms">
            <ServiceLineSynonyms />
          </TabPanel>
          <TabPanel header="Service Line Libraries">
            <ServiceLineLibraries />
          </TabPanel>
        </TabView>
      </TabPanel>
    </TabView>
  );
}
